import React from 'react';
import { KIND_LABEL, fastestIndex } from '../utils/splitRows';

// Compact segment table (mobile, RTL): # | distance | time | pace | HR. A work/rest chip appears only
// when the row's kind is known for certain (see utils/splitRows.js). Above it (Graphite): one bar per row, height by
// speed, so the fastest row is the tallest bar and the only lime one. A value the row lacks is left blank, never "—".
const COLS = '40px minmax(0,1fr) minmax(0,1fr) minmax(0,1fr) 40px';
const KIND_STYLE = {
  work: { border: '1px solid var(--g-line)', color: 'var(--text)' },
  rest: { border: '1px solid var(--g-line)', color: 'var(--text-muted)' },
  recovery: { border: '1px solid var(--g-line)', color: 'var(--text-muted)' },
  warmup: { border: '1px solid var(--g-line)', color: 'var(--text-muted)' },
  cooldown: { border: '1px solid var(--g-line)', color: 'var(--text-muted)' },
};

export default function SplitsTable({ rows, firstLabel = '#', footnote }) {
  if (!rows || rows.length === 0) return null;
  const best = fastestIndex(rows);
  const speeds = rows.map((r) => (r.paceSec > 0 ? 1 / r.paceSec : 0));
  const top = Math.max(...speeds);
  const low = Math.min(...speeds.filter((v) => v > 0));
  const height = (v) => (v > 0 && top > 0 ? (top === low ? 100 : 30 + 70 * ((v - low) / (top - low))) : 0);
  return (
    <div data-testid="splits-table">
      {top > 0 && (
        <div className="flex items-end gap-1 h-16 px-3 pt-3" aria-hidden="true" data-testid="splits-bars">
          {rows.map((r, i) => (
            <span key={i} className={`flex-1 min-w-[3px] rounded-t-[2px] ${i === best ? 'bg-accent' : 'bg-bar'}`}
                  style={{ height: `${height(speeds[i])}%` }} />
          ))}
        </div>
      )}
      <div className="grid gap-1.5 px-3 py-2 font-semibold text-[12px] text-text-muted border-b border-divider"
           style={{ gridTemplateColumns: COLS }}>
        <span>{firstLabel}</span><span>מרחק</span><span>זמן</span><span>קצב</span><span>דופק</span>
      </div>
      {rows.map((r, i) => (
        <div key={i} data-testid="split-row" data-kind={r.kind || ''}
             className="grid gap-1.5 items-center px-3 py-2 border-b border-divider last:border-b-0"
             style={{ gridTemplateColumns: COLS, opacity: r.kind === 'rest' || r.kind === 'recovery' ? 0.8 : 1 }}>
          <span className="flex flex-col items-start leading-tight">
            <span className="font-bold text-[14px] text-text tabular-nums">{r.n}</span>
            {r.kind && (
              <span data-testid="split-kind" className="mt-0.5 px-1 rounded text-[10px] font-semibold whitespace-nowrap"
                    style={KIND_STYLE[r.kind]}>{KIND_LABEL[r.kind]}</span>
            )}
          </span>
          <span className="font-medium text-[14px] text-text-body tabular-nums" dir="ltr" style={{ textAlign: 'right' }}>{r.distance || ''}</span>
          <span data-testid="split-time" className="font-medium text-[14px] text-text-body tabular-nums" dir="ltr" style={{ textAlign: 'right' }}>{r.time || ''}</span>
          <span className="font-bold text-[14px] tabular-nums" dir="ltr"
                style={{ textAlign: 'right', color: i === best ? 'var(--accent)' : 'var(--text)' }}>{r.pace || ''}</span>
          <span className="font-medium text-[14px] text-text-body tabular-nums">{r.hr ?? ''}</span>
        </div>
      ))}
      {footnote && <div className="px-3 py-2 font-medium text-[12px] text-text-muted">{footnote}</div>}
    </div>
  );
}
